import { z } from "zod";
import {
  dateSchema,
  timeSchema,
  firestoreTimestampSchema,
  locationEnum,
  visitTypeEnum,
  appointmentStatusEnum,
  hexColorSchema,
  strongPasswordSchema,
} from "./common.schema";
import { examRefSchema } from "./exam.schema";

// ============================================
// APPOINTMENT ENTITY SCHEMA
// ============================================

export const appointmentSchema = z.object({
  id: z.string().optional(),

  // Patient reference
  patientId: z.string(),
  patientName: z.string(), // Denormalized
  patientEligibility: z.enum(["idoneo", "non_idoneo"]).optional().nullable(), // Populated on read
  patientJobRoleName: z.string().optional(), // Denormalized
  patientDateOfBirth: dateSchema.optional(),
  patientPlaceOfBirth: z.string().optional(),

  // Doctor reference - optional for analysis-only bookings
  doctorId: z.string().optional().nullable(),
  doctorName: z.string().optional().nullable(), // Denormalized

  // Analysis-only flag (booking without doctor)
  analysisOnly: z.boolean().optional().default(false),

  // Company reference
  companyId: z.string(),
  companyName: z.string(), // Denormalized

  // Date/Time
  date: dateSchema,
  time: timeSchema,
  datetime: firestoreTimestampSchema, // Combined for sorting/querying
  // Snapshot of the effective slot duration. Legacy documents omit it and use 15.
  durationMinutes: z.number().int().min(6).max(120).optional(),

  // Appointment details
  location: locationEnum,
  visitType: visitTypeEnum,
  examIds: z.array(z.string()).optional(),
  exams: z.array(examRefSchema).optional(),

  // Google Calendar integration
  googleEventId: z.string().optional(),

  // Notes
  notes: z.string().optional(),
  doctorNote: z.string().optional(), // Doctor's private note

  // Patient call tracking
  patientCalled: z.boolean().optional(),
  calledAt: firestoreTimestampSchema.optional(),
  calledBy: z.string().optional(),

  // Status
  status: appointmentStatusEnum.optional().default("prenotata"),
  statusChangedAt: firestoreTimestampSchema.optional(),
  statusChangedBy: z.string().optional(),

  // Acceptance
  acceptance: z.boolean().optional().default(false),

  // Audit
  createdBy: z.string(),
  createdAt: firestoreTimestampSchema.optional(),
  updatedAt: firestoreTimestampSchema.optional(),
  documentUrl: z.string().optional(),
  documentId: z.string().optional(),
});

export type Appointment = z.infer<typeof appointmentSchema>;

// ============================================
// CREATE APPOINTMENT DTO
// ============================================

export const createAppointmentSchema = z
  .object({
    // ============================================
    // PATIENT FIELDS
    // ============================================
    patientId: z.string().optional(),
    patientFirstName: z.string().optional(),
    patientLastName: z.string().optional(),
    patientDateOfBirth: dateSchema.optional(),
    patientPlaceOfBirth: z.string().optional(),
    patientJobRoleId: z.string().optional(),
    patientCf: z.string().optional(),
    patientGender: z.string().optional(),
    patientEligibility: z.enum(["idoneo", "non_idoneo"]).optional().nullable(),

    // ============================================
    // DOCTOR FIELDS
    // ============================================
    doctorId: z.string().optional(),
    doctorEmail: z
      .string()
      .email("Doctor email must be a valid email address")
      .optional(),
    doctorName: z.string().optional(),
    doctorSpecialization: z.string().optional(),
    doctorColor: hexColorSchema.optional(),
    doctorPassword: strongPasswordSchema.optional(),
    doctorCompanyId: z.string().optional(),
    documentUrl: z.string().optional(),
    documentId: z.string().optional(),

    // ============================================
    // COMPANY FIELDS
    // ============================================
    companyId: z.string().optional(),
    companyName: z.string().optional(),
    companyAddress: z.string().optional(),
    companyVatNumber: z.string().optional(),
    companyPrimaryDoctorId: z.string().optional(),
    companyCoordinationDoctorIds: z.array(z.string()).optional(),
    companyGeslabId: z.string().optional(),
    companyEmail: z.string().email().optional(),

    // ============================================
    // APPOINTMENT FIELDS
    // ============================================
    date: dateSchema,
    time: timeSchema,
    location: locationEnum,
    visitType: visitTypeEnum,
    examIds: z.array(z.string()).optional(), // IDs to lookup exams

    notes: z.string().optional(),
    doctorNote: z.string().optional(),
    forceBooking: z.boolean().optional(), // Bypass conflict check

    // Confirmation Emails
    sendCompanyConfirmationEmail: z.boolean().optional(),
    sendPatientConfirmationEmail: z.boolean().optional(),

    // Analysis-only mode (no doctor required)
    analysisOnly: z.boolean().optional().default(false),
  })
  // Validate doctor: either ID or required fields (skip if analysisOnly)
  .refine(
    (data) => {
      // Skip doctor validation for analysis-only bookings
      if (data.analysisOnly) return true;
      if (data.doctorId) return true;
      return (
        !!data.doctorEmail &&
        !!data.doctorName &&
        !!data.doctorSpecialization &&
        !!data.doctorPassword
      );
    },
    {
      message:
        "Doctor email, name, specialization, and password are required when creating new doctor",
      path: ["doctorId"],
    },
  )
  // Validate company: either ID or required fields
  .refine(
    (data) => {
      if (data.companyId) return true;
      return !!data.companyName;
    },
    {
      message: "Company name is required when creating new company",
      path: ["companyId"],
    },
  );

export type CreateAppointment = z.infer<typeof createAppointmentSchema>;

// ============================================
// UPDATE APPOINTMENT DTO
// ============================================

export const updateAppointmentSchema = z
  .object({
    // ============================================
    // PATIENT FIELDS
    // ============================================
    patientId: z.string().optional(),
    patientName: z.string().optional(), // Denormalized
    patientFirstName: z.string().optional(),
    patientLastName: z.string().optional(),
    patientDateOfBirth: dateSchema.optional(),
    patientPlaceOfBirth: z.string().optional(),
    patientJobRoleId: z.string().optional(),
    patientCf: z.string().optional(),
    patientGender: z.string().optional(),
    patientEligibility: z.enum(["idoneo", "non_idoneo"]).optional().nullable(),

    // ============================================
    // DOCTOR FIELDS
    // ============================================
    doctorId: z.string().optional().nullable(),
    doctorName: z.string().optional().nullable(), // Denormalized
    doctorEmail: z
      .string()
      .email("Doctor email must be a valid email address")
      .optional(),
    doctorSpecialization: z.string().optional(),
    doctorColor: hexColorSchema.optional(),
    doctorPassword: strongPasswordSchema.optional(),
    doctorCompanyId: z.string().optional(),

    // ============================================
    // COMPANY FIELDS
    // ============================================
    companyId: z.string().optional(),
    companyName: z.string().optional(), // Denormalized
    companyAddress: z.string().optional(),
    companyVatNumber: z.string().optional(),
    companyPrimaryDoctorId: z.string().optional(),
    companyCoordinationDoctorIds: z.array(z.string()).optional(),
    companyGeslabId: z.string().optional(),
    companyEmail: z.string().email().optional(),

    // ============================================
    // APPOINTMENT FIELDS
    // ============================================
    date: dateSchema.optional(),
    time: timeSchema.optional(),

    // Details
    location: locationEnum.optional(),
    visitType: visitTypeEnum.optional(),
    examIds: z.array(z.string()).optional(),

    // Notes
    notes: z.string().optional(),
    doctorNote: z.string().optional(),

    // Options
    forceBooking: z.boolean().optional(),

    // Confirmation Emails
    sendCompanyConfirmationEmail: z.boolean().optional(),
    sendPatientConfirmationEmail: z.boolean().optional(),

    // Analysis-only mode
    analysisOnly: z.boolean().optional(),
    documentUrl: z.string().optional(),
    documentId: z.string().optional(),

    status: appointmentStatusEnum.optional(),
  })
  // Validation: if creating new doctor, require fields
  .refine(
    (data) => {
      if (data.doctorEmail && !data.doctorId) {
        return (
          data.doctorEmail &&
          data.doctorName &&
          data.doctorSpecialization &&
          data.doctorPassword
        );
      }
      return true;
    },
    {
      message:
        "Doctor email, name, specialization, and password are required when creating new doctor",
      path: ["doctorId"],
    },
  )
  // Validation: if creating new company, require name
  .refine(
    (data) => {
      if (!data.companyId && data.companyName) {
        return !!data.companyName;
      }
      return true;
    },
    {
      message: "Company name is required when creating new company",
      path: ["companyId"],
    },
  );

export type UpdateAppointment = z.infer<typeof updateAppointmentSchema>;

// ============================================
// UPDATE STATUS DTO
// ============================================

export const updateAppointmentStatusSchema = z.object({
  status: appointmentStatusEnum,
});

export type UpdateAppointmentStatus = z.infer<
  typeof updateAppointmentStatusSchema
>;

// ============================================
// UPDATE ACCEPTANCE DTO
// ============================================

export const updateAppointmentAcceptanceSchema = z.object({
  acceptance: z.boolean(),
});

export type UpdateAppointmentAcceptance = z.infer<
  typeof updateAppointmentAcceptanceSchema
>;
